import React from 'react'
import { PublishedBroadcast, ExposureCoverage, ExposureFrames } from '../../../services/broadcastService'
import { SPOT_SECONDS, formatMoney } from '../broadcastValuation'

interface Props {
  broadcast: PublishedBroadcast
  coverage: ExposureCoverage[]
  frames: ExposureFrames | null
  rate: number | null
  currency: string
}

/**
 * How the numbers above were produced, and what they do not cover.
 *
 * NOT AN APPENDIX. This report is sent to a sponsor who will challenge it, and
 * every caveat here is one they would otherwise find for themselves and read as
 * something we concealed. Stating the sampling rate, the unreadable signage and
 * the fact that the rate is an assumption costs less than being asked.
 */
const ExposureMethod: React.FC<Props> = ({ broadcast: b, coverage, frames, rate, currency }) => {
  const named = coverage.reduce((s, c) => s + c.named, 0)
  const unreadable = coverage.reduce((s, c) => s + c.unreadable, 0)
  const pctUnreadable = named + unreadable > 0
    ? (unreadable / (named + unreadable)) * 100
    : null

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">How this was measured</h3>
      <dl className="space-y-2.5 text-[11px] leading-relaxed text-gray-600">
        <div>
          <dt className="font-semibold text-gray-700 inline">Sampling. </dt>
          <dd className="inline">
            One frame per second across the broadcast
            {frames && <> — {frames.analysed.toLocaleString()} frames analysed,{' '}
              {frames.withDetections.toLocaleString()} carrying at least one brand</>}.
            A logo on screen for a fraction of a sampled second counts as a whole second.
          </dd>
        </div>

        <div>
          <dt className="font-semibold text-gray-700 inline">Area-seconds. </dt>
          <dd className="inline">
            Each detection&rsquo;s bounding box as a share of the frame, multiplied by the
            time it held. One area-second is a brand filling the entire frame for one
            second. Boxes covering virtually the whole frame are discarded as failures to
            localise rather than counted as full-screen exposure.
          </dd>
        </div>

        {rate !== null && (
          <div>
            <dt className="font-semibold text-gray-700 inline">Value. </dt>
            <dd className="inline">
              Area-seconds &divide; {SPOT_SECONDS} &times; {formatMoney(rate, currency)} per
              :{SPOT_SECONDS}, giving the cost of buying the same screen time as
              advertising. The rate comes from this broadcast&rsquo;s own audience and CPM
              and is a <strong className="font-medium">planning assumption, not a
              confirmed rate card</strong>. No weighting is applied for placement,
              obstruction or sharpness.
            </dd>
          </div>
        )}

        <div>
          <dt className="font-semibold text-gray-700 inline">What counts. </dt>
          <dd className="inline">
            Third-party brands only. Club, league, venue and broadcaster marks are
            classified separately and excluded from value, as is anything a reviewer
            rejected. Brands can appear simultaneously, so their times sum to more than
            the length of the broadcast.
          </dd>
        </div>

        {pctUnreadable !== null && pctUnreadable > 0 && (
          <div>
            <dt className="font-semibold text-gray-700 inline">Unreadable signage. </dt>
            <dd className="inline">
              {pctUnreadable.toFixed(1)}% of signage occurrences were visible but could not
              be identified — too small, too blurred or too obscured to name. Those carry
              no value here, so this report understates total exposure.
            </dd>
          </div>
        )}

        <div>
          <dt className="font-semibold text-gray-700 inline">Scope. </dt>
          <dd className="inline">
            Exposure within the programme only
            {b.venue_name && <>, at {b.venue_name}</>}. Commercial spots are not measured.
            {b.child_count > 0 && <> Includes {b.child_count} linked feed
              {b.child_count === 1 ? '' : 's'}.</>}
          </dd>
        </div>
      </dl>
    </div>
  )
}

export default ExposureMethod
